import type { DropoutReason } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import { DROPOUT_REASON_KEYS } from './participant-state'

/** Longueur de la précision libre, la même borne que l'API (`MAX_DROP_NOTE_LENGTH`). */
const MAX_NOTE = 500

type IProps = {
  open: boolean
  /** Nom de l'apprenant, dans le titre : on sait qui l'on déclare. */
  learnerName: string
  busy: boolean
  onCancel: () => void
  onConfirm: (input: { reason: DropoutReason, note: string }) => void
}

/** Déclarer un abandon (#324) : un motif à choisir, une précision facultative. */
function DropoutDialog({ open, learnerName, busy, onCancel, onConfirm }: IProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState<DropoutReason | ''>('')
  const [note, setNote] = useState('')

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (reason) onConfirm({ reason, note })
  }

  return (
    <Dialog open={open} onOpenChange={value => !value && onCancel()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t('sessionDetail.dropout.title', { name: learnerName })}</DialogTitle>
            <DialogDescription>{t('sessionDetail.dropout.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dropoutReason" required>{t('sessionDetail.dropout.reason.label')}</Label>
            <select
              id="dropoutReason"
              value={reason}
              onChange={e => setReason(e.target.value as DropoutReason)}
              required
              className="h-10 rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="" disabled>{t('sessionDetail.dropout.reason.placeholder')}</option>
              {Object.entries(DROPOUT_REASON_KEYS).map(([value, key]) => (
                <option key={value} value={value}>{t(key)}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dropoutNote">{t('sessionDetail.dropout.note')}</Label>
            <textarea
              id="dropoutNote"
              rows={3}
              maxLength={MAX_NOTE}
              value={note}
              onChange={e => setNote(e.target.value)}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!reason || busy}>{t('sessionDetail.dropout.confirm')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default DropoutDialog
